import { useEffect, useRef, useState } from 'react';
import { Eraser, Pencil, Printer, RotateCcw, Undo2, Check, Camera, Type, X, MousePointerClick } from 'lucide-react';
import { SketchTextPanel, FONTS, type TextStyle } from '@generated/components/SketchTextPanel';

interface Props { sizeName: string; widthIn: number; heightIn: number; saved: File | null; onSave: (f: File | null) => void }
type TextItem = TextStyle & { id: number; x: number; y: number };
type Action = { kind: 'stroke'; img: ImageData } | { kind: 'text'; id: number };

const PX_PER_IN = 60; // drawing resolution
const fontCss = (t: TextStyle, px: number) => `${t.bold ? 'bold ' : ''}${px}px ${FONTS[t.font]?.css ?? 'serif'}`;

/** A blank, outlined box in the shape of the ad they bought — they sketch their idea in it, and type movable text. */
export function SketchPad({ sizeName, widthIn, heightIn, saved, onSave }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const history = useRef<Action[]>([]);
  const drag = useRef<{ id: number; dx: number; dy: number } | null>(null);
  const nextId = useRef(1);
  const [tool, setTool] = useState<'pen' | 'thick' | 'eraser' | 'text'>('pen');
  const [dirty, setDirty] = useState(false);
  const [style, setStyle] = useState<TextStyle>({ text: '', font: 0, pt: 24, bold: true, align: 'center' });
  const [texts, setTexts] = useState<TextItem[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [inline, setInline] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const w = Math.round(widthIn * PX_PER_IN), h = Math.round(heightIn * PX_PER_IN);
  const selected = texts.find((t) => t.id === sel) ?? null;

  const ctx = () => ref.current?.getContext('2d') ?? null;
  const changed = () => { setDirty(true); onSave(null); };
  const clear = () => { const c = ctx(); if (c) { c.fillStyle = '#fff'; c.fillRect(0, 0, w, h); } history.current = []; setTexts([]); setSel(null); setDirty(false); onSave(null); };
  useEffect(() => { clear(); }, [w, h]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / w || 1));
    ro.observe(el); return () => ro.disconnect();
  }, [w]);

  const toCanvas = (clientX: number, clientY: number) => {
    const r = ref.current?.getBoundingClientRect();
    return r ? { x: ((clientX - r.left) / r.width) * w, y: ((clientY - r.top) / r.height) * h } : { x: 0, y: 0 };
  };
  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = ctx(); if (!c) return;
    const p = toCanvas(e.clientX, e.clientY);
    if (tool === 'text') {
      if (sel !== null) { setSel(null); return; }
      if (!style.text.trim()) return;
      const id = nextId.current++;
      setTexts((t) => [...t, { ...style, id, x: p.x, y: p.y }]);
      history.current.push({ kind: 'text', id });
      setSel(id); changed();
      return;
    }
    setSel(null);
    e.currentTarget.setPointerCapture(e.pointerId);
    history.current.push({ kind: 'stroke', img: c.getImageData(0, 0, w, h) });
    if (history.current.length > 40) history.current.shift();
    drawing.current = true;
    c.lineCap = 'round'; c.lineJoin = 'round';
    c.strokeStyle = tool === 'eraser' ? '#fff' : '#111';
    c.lineWidth = tool === 'eraser' ? 24 : tool === 'thick' ? 9 : 3;
    c.beginPath(); c.moveTo(p.x, p.y); c.lineTo(p.x + 0.1, p.y + 0.1); c.stroke();
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const c = ctx(); if (!c) return;
    const p = toCanvas(e.clientX, e.clientY); c.lineTo(p.x, p.y); c.stroke();
  };
  const up = () => { if (drawing.current) { drawing.current = false; changed(); } };
  const undo = () => {
    const last = history.current.pop(); if (!last) return;
    if (last.kind === 'stroke') ctx()?.putImageData(last.img, 0, 0);
    else { setTexts((t) => t.filter((x) => x.id !== last.id)); if (sel === last.id) setSel(null); }
    onSave(null);
  };

  // Text dragging
  const tDown = (e: React.PointerEvent<HTMLDivElement>, t: TextItem) => {
    if (inline === t.id) return;
    e.stopPropagation(); e.currentTarget.setPointerCapture(e.pointerId);
    const p = toCanvas(e.clientX, e.clientY);
    drag.current = { id: t.id, dx: p.x - t.x, dy: p.y - t.y };
    setSel(t.id); setStyle({ text: t.text, font: t.font, pt: t.pt, bold: t.bold, align: t.align }); setTool('text');
  };
  const tMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current; if (!d) return;
    const p = toCanvas(e.clientX, e.clientY);
    setTexts((list) => list.map((x) => (x.id === d.id ? { ...x, x: Math.max(0, Math.min(w, p.x - d.dx)), y: Math.max(0, Math.min(h, p.y - d.dy)) } : x)));
  };
  const tUp = () => { if (drag.current) { drag.current = null; changed(); } };
  const onStyle = (s: TextStyle) => {
    setStyle(s);
    if (sel !== null) { setTexts((list) => list.map((x) => (x.id === sel ? { ...x, ...s } : x))); changed(); }
  };
  const editWords = (id: number, value: string) => { setTexts((list) => list.map((x) => (x.id === id ? { ...x, text: value } : x))); setStyle((s) => ({ ...s, text: value })); changed(); };
  const removeSel = () => { if (sel === null) return; setTexts((t) => t.filter((x) => x.id !== sel)); setSel(null); changed(); };

  const save = () => {
    const base = ref.current; if (!base) return;
    const out = document.createElement('canvas'); out.width = w; out.height = h;
    const c = out.getContext('2d'); if (!c) return;
    c.drawImage(base, 0, 0);
    c.fillStyle = '#111'; c.textBaseline = 'top';
    for (const t of texts) {
      c.textAlign = t.align;
      const px = (t.pt * PX_PER_IN) / 72; c.font = fontCss(t, px);
      t.text.split('\n').forEach((line, i) => c.fillText(line, t.x, t.y + i * px * 1.15));
    }
    out.toBlob((b) => { if (b) onSave(new File([b], `ad-sketch-${sizeName.replace(/\s+/g, '-').toLowerCase()}.png`, { type: 'image/png' })); }, 'image/png');
  };
  const printBlank = () => {
    const win = window.open('', '_blank'); if (!win) return;
    win.document.write(`<html><head><title>${sizeName} ad sketch</title><style>@page{margin:0.5in}body{font-family:sans-serif}div{width:${widthIn}in;height:${heightIn}in;border:2px solid #000;box-sizing:border-box}</style></head><body><p>${sizeName} — ${widthIn}" × ${heightIn}" (actual size). Sketch your ad inside the box, then take a photo and upload it.</p><div></div><script>window.onload=()=>window.print()</script></body></html>`);
    win.document.close();
  };

  const tb = (on: boolean) => `inline-flex min-h-11 items-center gap-1 border px-3 text-xs font-semibold ${on ? 'border-foreground bg-foreground text-background' : 'border-border bg-background'}`;
  return (
    <div>
      <p className="text-sm text-muted-foreground">This box is the shape of your <strong className="text-foreground">{sizeName}</strong> ad ({widthIn}" wide × {heightIn}" tall), shown close to actual size. Draw or type roughly where you'd like things: your name, a picture, the main message. It doesn't need to be neat.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => { setTool('pen'); setSel(null); }} className={tb(tool === 'pen')}><Pencil className="h-3.5 w-3.5" />Pen</button>
        <button type="button" onClick={() => { setTool('thick'); setSel(null); }} className={tb(tool === 'thick')}><Pencil className="h-4 w-4" />Marker</button>
        <button type="button" onClick={() => setTool('text')} className={tb(tool === 'text')}><Type className="h-3.5 w-3.5" />Type text</button>
        <button type="button" onClick={() => { setTool('eraser'); setSel(null); }} className={tb(tool === 'eraser')}><Eraser className="h-3.5 w-3.5" />Eraser</button>
        <button type="button" onClick={undo} className={tb(false)}><Undo2 className="h-3.5 w-3.5" />Undo</button>
        <button type="button" onClick={clear} className={tb(false)}><RotateCcw className="h-3.5 w-3.5" />Start over</button>
      </div>
      {tool === 'text' && <SketchTextPanel style={style} onChange={onStyle} editing={!!selected} onDone={() => setSel(null)} onDelete={removeSel} />}
      <div className="mt-3 overflow-x-auto bg-muted p-3 sm:p-4">
        <div className="relative max-w-full" style={{ width: `${widthIn}in` }}>
          <canvas ref={ref} width={w} height={h} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
            aria-label={`Drawing area shaped like a ${sizeName} ad`} style={{ aspectRatio: `${widthIn} / ${heightIn}` }}
            className="block h-auto w-full touch-none border-2 border-foreground bg-background" />
          {tool === "text" && sel === null && style.text.trim() !== "" && (
            <div className="pointer-events-none absolute inset-x-2 top-2 z-10 flex items-center justify-center gap-2 bg-[hsl(var(--brand-blue))] px-3 py-2 text-center text-sm font-semibold text-primary-foreground">
              <MousePointerClick className="h-4 w-4 shrink-0" />Now tap in the box to place your words. Then drag them where you want.
            </div>
          )}
          {texts.map((t) => (
            <div key={t.id} role="button" tabIndex={0} aria-label={`Text: ${t.text}. Drag to move.`}
              onPointerDown={(e) => tDown(e, t)} onPointerMove={tMove} onPointerUp={tUp} onDoubleClick={() => { setSel(t.id); setInline(t.id); }}
              onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Delete' || e.key === 'Backspace')) removeSel(); }}
              className={`absolute cursor-move touch-none select-none whitespace-pre leading-[1.15] text-foreground ${sel === t.id ? 'outline-dashed outline-2 outline-[hsl(var(--brand-blue))]' : 'hover:outline-dotted hover:outline-1'}`}
              style={{ left: `${(t.x / w) * 100}%`, top: `${(t.y / h) * 100}%`, transform: t.align === 'left' ? 'none' : t.align === 'right' ? 'translateX(-100%)' : 'translateX(-50%)', textAlign: t.align, font: fontCss(t, ((t.pt * PX_PER_IN) / 72) * scale) }}>
              {inline === t.id
                ? <textarea autoFocus value={t.text} aria-label="Edit the words" onChange={(e) => editWords(t.id, e.target.value)} onBlur={() => setInline(null)} onPointerDown={(e) => e.stopPropagation()}
                    rows={Math.max(1, t.text.split("\n").length)} className="block resize-none bg-background/90 outline-none" style={{ font: "inherit", textAlign: t.align, width: `${Math.max(4, ...t.text.split("\n").map((l) => l.length)) + 2}ch` }} />
                : t.text}
              {sel === t.id && inline !== t.id && <button type="button" aria-label="Change the words" onPointerDown={(e) => e.stopPropagation()} onClick={() => setInline(t.id)} className="absolute -left-4 -top-4 flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--brand-blue))] text-primary-foreground"><Pencil className="h-3.5 w-3.5" /></button>}
              {sel === t.id && <button type="button" aria-label="Delete this text" onPointerDown={(e) => e.stopPropagation()} onClick={removeSel} className="absolute -right-4 -top-4 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground"><X className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={save} disabled={!dirty} className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-blue))] px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">
          {saved ? <><Check className="h-4 w-4" />Sketch attached to your order</> : 'Attach my sketch'}
        </button>
        <button type="button" onClick={printBlank} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold"><Printer className="h-4 w-4" />Print a blank box instead</button>
        <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 border border-foreground px-4 text-sm font-semibold">
          <Camera className="h-4 w-4" />Upload a photo of my paper sketch
          <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) { onSave(f); setDirty(false); } }} />
        </label>
      </div>
      {saved && <p className="mt-2 text-xs text-muted-foreground">Attached: {saved.name}. It will be sent with your order.</p>}
    </div>
  );
}
